import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  fetchTeams,
  fetchTeamsWithAdvancedFilters,
  updateTeam,
  updateTeamName,
  teamsQueryKeys,
  UpdateTeamData,
} from '../api/teams.api';
import { TeamsQueryParams, TeamsQueryResult, Team } from '../types/team.types';
import { AdvancedFilterConfig } from '../../types/filter';

/**
 * Hook para obtener la lista de equipos con TanStack Query
 */
export function useTeams(
  params?: TeamsQueryParams,
  options?: Omit<UseQueryOptions<TeamsQueryResult, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: teamsQueryKeys.list(params),
    queryFn: () => fetchTeams(params),
    staleTime: 1000 * 60 * 5, // 5 minutos
    ...options,
  });
}

/**
 * Hook para obtener equipos con filtros avanzados del FilterPopup
 */
export function useTeamsWithFilters(
  advancedFilters: AdvancedFilterConfig[],
  params?: Omit<TeamsQueryParams, 'filters'>,
  options?: Omit<UseQueryOptions<TeamsQueryResult, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...teamsQueryKeys.lists(), 'filtered', { advancedFilters, params }],
    queryFn: () => fetchTeamsWithAdvancedFilters(advancedFilters, params),
    staleTime: 1000 * 60 * 5, // 5 minutos
    ...options,
  });
}

/**
 * Hook que combina la tabla con TanStack Query
 * Gestiona automáticamente paginación, ordenamiento y filtros
 */
export interface UseTeamsTableOptions {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  filters?: AdvancedFilterConfig[];
  search?: string;
  enabled?: boolean;
}

export function useTeamsTable(options: UseTeamsTableOptions = {}) {
  const {
    page = 1,
    pageSize = 10,
    sortField,
    sortDirection = 'asc',
    filters = [],
    search,
    enabled = true,
  } = options;

  const queryClient = useQueryClient();

  // Query principal
  const query = useQuery({
    queryKey: [
      ...teamsQueryKeys.lists(),
      'table',
      { page, pageSize, sortField, sortDirection, filters, search },
    ],
    queryFn: async () => {
      if (filters.length > 0) {
        return fetchTeamsWithAdvancedFilters(filters, {
          page,
          pageSize,
          sortField,
          sortDirection,
        });
      }

      return fetchTeams({
        page,
        pageSize,
        sortField,
        sortDirection,
        search,
      });
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    // Mantener datos anteriores mientras se cargan los nuevos (evita el "brinco")
    placeholderData: keepPreviousData,
    enabled,
  });

  // Prefetch de la siguiente página para mejor UX
  const prefetchNextPage = () => {
    if (query.data && page < query.data.pagination.pageCount) {
      queryClient.prefetchQuery({
        queryKey: [
          ...teamsQueryKeys.lists(),
          'table',
          { page: page + 1, pageSize, sortField, sortDirection, filters, search },
        ],
        queryFn: () => {
          if (filters.length > 0) {
            return fetchTeamsWithAdvancedFilters(filters, {
              page: page + 1,
              pageSize,
              sortField,
              sortDirection,
            });
          }
          return fetchTeams({
            page: page + 1,
            pageSize,
            sortField,
            sortDirection,
            search,
          });
        },
      });
    }
  };

  // Invalidar y refetch
  const refetch = () => {
    return queryClient.invalidateQueries({
      queryKey: teamsQueryKeys.lists(),
    });
  };

  // Paginación directa de Strapi (sin cálculos propios)
  const strapiPagination = query.data?.pagination;

  return {
    // Data
    data: query.data?.data ?? [],

    // Paginación de Strapi (directamente como viene)
    pagination: strapiPagination ?? null,

    // Estado de la query
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,
    // true cuando se muestran datos anteriores mientras se cargan los nuevos
    isPlaceholderData: query.isPlaceholderData,

    // Acciones
    refetch,
    prefetchNextPage,

    // Query completa para casos avanzados
    query,
  };
}

/**
 * Hook para invalidar el cache de equipos
 */
export function useInvalidateTeams() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: teamsQueryKeys.all }),
    invalidateLists: () => queryClient.invalidateQueries({ queryKey: teamsQueryKeys.lists() }),
  };
}

/**
 * Variables para la mutación de actualización
 */
export interface UpdateTeamVariables {
  id: number;
  data: UpdateTeamData;
}

// Tipo para el contexto del optimistic update
type OptimisticContext = {
  previousData: [readonly unknown[], unknown][];
};

/**
 * Hook para actualizar un equipo con optimistic updates
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation<Team, Error, UpdateTeamVariables, OptimisticContext>({
    mutationFn: ({ id, data }) => updateTeam(id, data),
    // Optimistic update: actualizar la UI antes de que el servidor responda
    onMutate: async ({ id, data }) => {
      // Cancelar queries en curso para evitar sobrescribir el optimistic update
      await queryClient.cancelQueries({ queryKey: teamsQueryKeys.all });

      // Guardar el estado anterior para poder revertir si hay error
      const previousData = queryClient.getQueriesData({ queryKey: teamsQueryKeys.all });

      // Actualizar optimisticamente todas las queries que contengan este equipo
      queryClient.setQueriesData<TeamsQueryResult | undefined>(
        { queryKey: teamsQueryKeys.all },
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((team) => (team.id === id ? { ...team, ...data } : team)),
          };
        }
      );

      return { previousData };
    },
    // Si hay error, revertir al estado anterior
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    // Siempre invalidar después para asegurar sincronización con el servidor
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: teamsQueryKeys.all });
    },
  });
}

/**
 * Hook para actualizar solo el nombre de un equipo con optimistic updates
 */
export function useUpdateTeamName() {
  const queryClient = useQueryClient();

  return useMutation<Team, Error, { id: number; nombre: string }, OptimisticContext>({
    mutationFn: ({ id, nombre }) => updateTeamName(id, nombre),
    // Optimistic update: actualizar la UI antes de que el servidor responda
    onMutate: async ({ id, nombre }) => {
      // Cancelar queries en curso para evitar sobrescribir el optimistic update
      await queryClient.cancelQueries({ queryKey: teamsQueryKeys.all });

      // Guardar el estado anterior para poder revertir si hay error
      const previousData = queryClient.getQueriesData({ queryKey: teamsQueryKeys.all });

      // Actualizar optimisticamente todas las queries que contengan este equipo
      queryClient.setQueriesData<TeamsQueryResult | undefined>(
        { queryKey: teamsQueryKeys.all },
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((team) => (team.id === id ? { ...team, nombre } : team)),
          };
        }
      );

      return { previousData };
    },
    // Si hay error, revertir al estado anterior
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    // Siempre invalidar después para asegurar sincronización con el servidor
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: teamsQueryKeys.all });
    },
  });
}
